import type { Clock, Millis, Seconds } from './types';

export interface ClockOptions {
  extraLatencyMs?: Millis;
}

export const MAX_OUTPUT_LATENCY: Seconds = 0.1;
export const MAX_EVENT_STALENESS_MS: Millis = 100;

interface LatencyReport {
  outputLatency?: number;
  baseLatency?: number;
}

export function toMs(seconds: Seconds): Millis {
  return seconds * 1000;
}

export function deltaMs(a: Seconds, b: Seconds): Millis {
  return (a - b) * 1000;
}

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function reportedLatency(context: BaseAudioContext): Seconds {
  const report = context as unknown as LatencyReport;
  const output = report.outputLatency;
  if (typeof output === 'number' && Number.isFinite(output) && output > 0) {
    return clamp(output, 0, MAX_OUTPUT_LATENCY);
  }
  const base = report.baseLatency;
  if (typeof base === 'number' && Number.isFinite(base) && base > 0) {
    return clamp(base, 0, MAX_OUTPUT_LATENCY);
  }
  return 0;
}

function calibrationLatency(extraLatencyMs: Millis): Seconds {
  if (!Number.isFinite(extraLatencyMs)) return 0;
  return clamp(extraLatencyMs / 1000, -MAX_OUTPUT_LATENCY, MAX_OUTPUT_LATENCY);
}

export function createClock(context: BaseAudioContext, options?: ClockOptions): Clock {
  const extra = calibrationLatency(options?.extraLatencyMs ?? 0);
  const latency = (): Seconds => Math.max(0, reportedLatency(context) + extra);
  const now = (): Seconds => context.currentTime - latency();

  return {
    now,
    atEvent(event: Event): Seconds {
      const heard = now();
      const stamp = event.timeStamp;
      if (!Number.isFinite(stamp) || stamp <= 0) return heard;
      const staleness = performance.now() - stamp;
      if (!Number.isFinite(staleness) || staleness < 0 || staleness > MAX_EVENT_STALENESS_MS) {
        return heard;
      }
      return heard - staleness / 1000;
    },
    get outputLatency(): Seconds {
      return latency();
    },
  };
}
